import { useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils/general";
import { generateThemeFromInput } from "@/services/api";
import { Theme } from "@/types/themes";
import { ImmersiveTextInput } from "./ImmersiveTextInput";
import { useToast } from "@/hooks/useToast";
import { GAME_IDEA_PLACEHOLDERS } from "@/lib/constants/placeholders";

interface GameGenesisInputProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onThemeGenerated: (themes: Theme[]) => void;
  showError: boolean;
  disabled?: boolean;
}

export function GameGenesisInput({
  value,
  onChange,
  onKeyDown,
  onThemeGenerated,
  showError,
  disabled
}: GameGenesisInputProps) {
  const [showImmersive, setShowImmersive] = useState(false);
  const [placeholder] = useState(() => {
    const randomIndex = Math.floor(Math.random() * GAME_IDEA_PLACEHOLDERS.length);
    return GAME_IDEA_PLACEHOLDERS[randomIndex];
  });
  const { toast } = useToast();

  const handleSubmit = async (text: string) => {
    try {
      const response = await generateThemeFromInput(text);

      if (response.success) {
        const themes = typeof response.data === 'string' 
          ? JSON.parse(response.data) 
          : response.data;
        onThemeGenerated(themes);
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: response.error || "Failed to generate theme. Please try again."
        });
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to generate theme. Please try again."
      });
    }
  };

  if (showImmersive) {
    return (
      <ImmersiveTextInput
        value={value}
        onChange={(newValue) => onChange({ target: { value: newValue } } as React.ChangeEvent<HTMLTextAreaElement>)}
        onSubmit={handleSubmit}
        onClose={() => setShowImmersive(false)}
        placeholder={placeholder}
      />
    );
  }

  return (
    <div className="w-full p-4 flex flex-col items-center justify-center gap-2">
      <div className="w-full max-w-xl relative">
        <Textarea
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onClick={() => !disabled && setShowImmersive(true)}
          placeholder={placeholder}
          className={cn(
            "w-full min-h-[100px] resize-none p-4 text-lg text-game-input-text",
            "bg-game-input-bg border-none rounded-2xl shadow-md",
            "placeholder:text-muted-foreground/50",
            disabled && "cursor-not-allowed opacity-50"
          )}
          disabled={disabled}
        />
        {showError && (
          <div className="absolute -bottom-6 left-0 text-sm text-red-500">
            Please enter at least 7 characters
          </div>
        )}
      </div>
    </div>
  );
} 